//TODO: move to foxden-version-controller-client or new repo
import { GraphQLClient } from 'graphql-request';

import {
  getSdk,
  QuoteResponse,
  Sdk,
  UpdateCoverageInput,
  UpdateCoverageOutput
} from '../generated/rating-quoting-graphql';
import getEnv from '../utils/getEnv';

export class RatingQuotingClient {
  sdk: Sdk;

  constructor(versionNumber: string) {
    const graphQLClient = new GraphQLClient(
      this.getRatingQuotingURL(versionNumber)
    );
    this.sdk = getSdk(graphQLClient);
  }

  /**
   * Returns a SDK of rating-quoting backend.
   * @returns a sdk that has all APIs of rating-quoting backend.
   */
  public getSDK(): Sdk {
    return this.sdk;
  }

  /**
   * getQuote
   * @param applicationId: the objectId of the application to get the workflowVersion of
   * @returns a Promise for a QuoteResponse object
   */
  public async getQuote(applicationId: string): Promise<QuoteResponse> {
    const result = await this.sdk.getQuote({ applicationId });
    return result.getQuote;
  }

  /**
   * sendApplicationGroupEmail
   *
   * Sends the single consolidated Foxden email covering every LOB in the group
   * (NCP-5129). Call this only once every getQuote for the group has resolved —
   * that is the only point at which every LOB's quote number exists.
   *
   * @param applicationGroupId: the objectId of the application group
   * @returns false when the group had no quoted LOB (forms-backend emailed it)
   */
  public async sendApplicationGroupEmail(
    applicationGroupId: string
  ): Promise<boolean> {
    const result = await this.sdk.sendApplicationGroupEmail({
      applicationGroupId
    });
    return result.sendApplicationGroupEmail;
  }

  /**
   * generateQuotePdf
   * @param quoteId: the objectId of latest quote
   * @returns a Promise for pdf Buffer
   */
  public async generateQuotePdf(quoteId: string): Promise<string> {
    const result = await this.sdk.generateQuotePdf({ quoteId });
    return result.generateQuotePdf;
  }

  /**
   * generateConsolidatedQuotePdf
   *
   * Builds one consolidated quote-proposal PDF for a group of quotes. Send the
   * list in document order — this client does not reorder it.
   *
   * @param quoteIds: the objectIds of the quotes in the group, in document order
   * @returns a Promise for the base64 PDF string
   */
  public async generateConsolidatedQuotePdf(
    quoteIds: string[]
  ): Promise<string> {
    const result = await this.sdk.generateConsolidatedQuotePdf({ quoteIds });
    return result.generateConsolidatedQuotePdf;
  }

  /**
   * updateCoverage
   * @param input: the coverage input of the quote which needed to be updated.
   * @returns a Promise for a QuoteResponse object
   */
  public async updateCoverage(
    input: UpdateCoverageInput
  ): Promise<UpdateCoverageOutput> {
    const result = await this.sdk.updateCoverage({ input });
    return result.updateCoverage;
  }

  private getRatingQuotingURL(versionNumber: string): string {
    const { REACT_APP_RATING_QUOTING_GRAPHQL_URL } = getEnv();

    // regex to replace version number in url
    const regex = /\d{4}-\d{2}-\d{2}/;
    return REACT_APP_RATING_QUOTING_GRAPHQL_URL.replace(regex, versionNumber);
  }
}
